import { Grid, Hidden } from '@material-ui/core';
import { Form, Formik, FormikProps } from 'formik';
import React, { useContext, useState } from 'react';
import Banner from '@cpg-its/ads.containers.banner';
import AuthContext from '@cpg-its/ads.auth.auth-context';
import { useSelector } from 'react-redux';
import { FormField } from '../../../../../../global/components/form-element/form-field';
import { FormWrapper } from '../../../../../../global/components/form-wrapper';
import { GuidedProcessSupportBlock } from '../../../../../../global/components/guided-process-support-block';
import { DividerSection } from '../../../../../../global/components/layout/divider-section';
import { ContentSpacer } from '../../../../../../global/components/layout/generic-content-spacer';
import { MainContent } from '../../../../../../global/components/layout/main-content-container';
import { PageContainer } from '../../../../../../global/components/layout/page-container';
import { PageHeader } from '../../../../../../global/components/layout/page-header';
import { SideBarContent } from '../../../../../../global/components/layout/sidebar-content';
import { useApiResponseError } from '../../../../../../global/hooks/useApiError';
import { FormTypes } from '../../../../../account-info/models/form-models';
import { useGpsType } from '../../../../../../global/hooks/useGpsType';
import { Institution } from '../../../../../../global/models/institution-model';
import { TcsmStates } from '../../../../../../root/tcsm-model';
import AdoptingInstitutionView from '../display-components/adopting-institution-view';
import { DbPlanFormModel } from '../models/plan-adoption-form-models';
import { WysiwygWithReactLink } from '../../../../../../global/components/wysiwyg-with-react-link';
import { PlanSummary } from '../../institution-model';
import { buttonStyles } from '../../../../../../global/constants/theme/styles/components/buttonstyles';
import DocusignView from '../../../docusign/docusign-view';
import { DocuSignRequest, DocuSignRequestOptions } from '../../../docusign/models/docusign-models';
import { Person } from '../../../../../../global/models/person-model';
import { DemographicAppKeys } from '../../../../../../global/models/demographics-model';
import { LayDbPlanAdoptionWizard } from '../wizards/lay-db-plan-adoption-wizard';
import { YesNoIndicators } from '../../../../../../global/models/api-model';
import { FormGroup } from '../../../../../../global/components/layout/form-group';
import { YesNoOptions } from '../../../../../../global/constants/lists';
import { QccoQuestionsShared } from '../shared/qcco-questions-shared';

interface Props {
	wizard: LayDbPlanAdoptionWizard;
}

function DbPlanAdoptionForm(props: Props) {

	const { wizard } = props;
	const { accessToken, decodedToken } = useContext(AuthContext) as any;
	const content = wizard?.getFormContentModel();
	const gpsItem = useGpsType(content?.gpsType);
	const apiErrors = useApiResponseError(FormTypes.ADOPT_INSTITUTION_PLAN_LAY_DB);

	// Split out errors that don't correspond to a DbPlanFormModel field — show them in a separate banner
	const dbPlanFormFields: (keyof DbPlanFormModel)[] = ['adoption_date', 'envelopeId', 'indemnificationEnvelopeId', 'complyEpiscopalChurchLayPensionSystem', 'qccoQuestion1', 'qccoQuestion2', 'qccoQuestion3'];
	const nonFieldApiErrors = Object.keys(apiErrors).filter(key => !dbPlanFormFields.includes(key as keyof DbPlanFormModel)).map(key => apiErrors[key]);
	const fieldApiErrors = Object.keys(apiErrors)
		.filter(key => dbPlanFormFields.includes(key as keyof DbPlanFormModel))
		.reduce((acc, key) => ({ ...acc, [key]: apiErrors[key] }), {});
	const currentInstitution = useSelector<TcsmStates, Institution>(state => state?.institutionState?.currentInstitution);
	const currentPlans = useSelector<TcsmStates, PlanSummary>(state => state?.institutionState?.planSummary);
	const needsIndemnify = wizard?.needIndemnify(currentPlans);
	const hasRsvp = wizard?.hasRsvp(currentPlans);
	const buttonClasses = buttonStyles();
	const [hideQuestion, setHideQuestion] = useState(false);
	const [indemnify, setIndemnify] = useState(needsIndemnify);
	const [rsvpComply, setRsvpComply] = useState(false);
	const labelSettings = wizard?.epiContent?.labelSettingState?.epiContent;
	const adoptionDateOptions = wizard?.getAdoptionDateOptions();
	const [showDocusignView, setShowDocusignView] = useState(false);
	const person = useSelector<TcsmStates, Person>(state => state?.personalInfo[DemographicAppKeys.myCPG].currentPerson);
	const onComplete = () => {
		setIndemnify(false);
		setShowDocusignView(false);
		setHideQuestion(true);
	};

	const docuSignRequest: DocuSignRequest = indemnify ? wizard?.prepareIndemnifyDocuSignRequest(person, currentInstitution, accessToken, decodedToken?.iat) : wizard?.prepareDocuSignRequest(person, currentInstitution, accessToken, decodedToken?.iat, needsIndemnify);
	const docuSignOptions: DocuSignRequestOptions = indemnify ? wizard.getIndemnifyDocusignOption(docuSignRequest, setShowDocusignView, onComplete, currentInstitution) : wizard?.getDocusignOption(docuSignRequest, setShowDocusignView, currentInstitution, needsIndemnify);
	function renderDocusignSignView() {
		if (!showDocusignView) return null;

		return <DocusignView options={docuSignOptions} />;
	}

	function complyChanged(e) {
		const { value } = e.target;
		if (value === YesNoIndicators.Yes) {
			setRsvpComply(true);
			setIndemnify(true);

		}
		if (value === YesNoIndicators.No) {
			setRsvpComply(false);
			setIndemnify(false);

		}
	}


	function renderQccoQuestions(formProps: FormikProps<DbPlanFormModel>) {
		return (
			<QccoQuestionsShared
				formProps={formProps}
				content={content}
				showStatus
				disabledOverlay={indemnify}
			/>
		);
	}

	function renderComplyEpiscopalChurchLayPensionSystemQuestion(formProps: FormikProps<DbPlanFormModel>) {

		if (!hasRsvp || hideQuestion) return null;

		return (
			<FormGroup>
				<FormField
					name='complyEpiscopalChurchLayPensionSystem'
					label={content?.complyEpiscopalChurchLayPensionSystemQuestion}
					component="radioGroup"
					options={YesNoOptions}
					touched={formProps.touched}
					errors={formProps.errors}
					onChange={(e) => complyChanged(e)}
					required
				/>
			</FormGroup>
		);
	}

	function renderNonFieldApiErrors() {
		if (!nonFieldApiErrors || nonFieldApiErrors.length === 0) return null;
		return (
			<ContentSpacer>
				<Banner
					banner={{
						type: 'error',
						label: <p><strong>{`${nonFieldApiErrors.length} error(s) were found.`}</strong></p>,
						description: <ul>
							{nonFieldApiErrors.map((message, idx) => (
								<li key={idx}>
									<p><WysiwygWithReactLink content={message} /></p>
								</li>
							))}
						</ul>,
					}}
				/>
			</ContentSpacer>
		);
	}

	return (
		<PageContainer>
			<MainContent>
				<Formik
					initialValues={wizard?.getFormInitialValue()}
					onSubmit={(values) => {
						const cleanedValues = { ...values };
						if (values.qccoQuestion1 === YesNoIndicators.Yes) {
							cleanedValues.qccoQuestion2 = YesNoIndicators.NotApplicable;
							cleanedValues.qccoQuestion3 = YesNoIndicators.NotApplicable;
						} else if (values.qccoQuestion1 === YesNoIndicators.No && values.qccoQuestion2 === YesNoIndicators.No) {
							cleanedValues.qccoQuestion3 = YesNoIndicators.NotApplicable;
						}
						
						wizard?.onSubmit({
							values: cleanedValues,
							clientId: currentInstitution?.org_client_nbr,
							toggleDocusign: setShowDocusignView,
						})();
					}}
					enableReinitialize
					initialErrors={fieldApiErrors}
					validationSchema={wizard?.getFormValidationSchema()}
				>
					{(formProps: FormikProps<DbPlanFormModel>) => (
						<>
							<Form>
								<Grid>
									<FormWrapper
										formikProps={formProps}
										onCancel={null}
										submitText=''
										cancelText=''
										hideButtons
									>
										{
											needsIndemnify && !indemnify && formProps?.values?.complyEpiscopalChurchLayPensionSystem !== YesNoIndicators.No &&
											<ContentSpacer>
												<Banner
													banner={{
														type: 'success',
														label: <WysiwygWithReactLink content={content?.indemnifySuccessMessage} />,
													}}
												/>

											</ContentSpacer>
										}
										{renderNonFieldApiErrors()}
										<PageHeader
											headerText={content?.pageTitle}
											description={needsIndemnify && indemnify ? null : content?.PageDescription}
										/>
										<Hidden mdUp>
											<SideBarContent>
												<GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
											</SideBarContent>
										</Hidden>
										{
											renderComplyEpiscopalChurchLayPensionSystemQuestion(formProps)
										}
								
										{
											((needsIndemnify && indemnify) || formProps?.values?.complyEpiscopalChurchLayPensionSystem === YesNoIndicators.No) &&
											<DividerSection disabledOverlay={hasRsvp && !rsvpComply} removeDivider >
												<ContentSpacer>
													<Banner
														banner={{
															type: 'error',
															label: wizard?.getIndemnifyWarningTitle(currentPlans),
															description: <>
																<WysiwygWithReactLink content={hasRsvp ? content?.indemnifyWarningRsvpMessage : content?.indemnifyWarningMessage} />
																<ContentSpacer>
																	<button className={buttonClasses.primaryButton} type='button' onClick={() => setShowDocusignView(true)}>{content?.indemnifyButtonText}</button>
																</ContentSpacer>
																<p>{content?.indemnifyButtonHelper}</p>
															</>,
														}}
													/>

												</ContentSpacer>
											</DividerSection>
										}

								{renderQccoQuestions(formProps)}
								<DividerSection disabledOverlay={indemnify} sectionText={content?.planAdoptionDetailTitle} removeDivider >
									<AdoptingInstitutionView institution={currentInstitution} clientNumberLabel={content?.clientIdLabel} sectionTitle={content?.adoptingInstitutionLabel} />
									<ContentSpacer>
										<FormField
											name='adoption_date'
											label={content?.adoptionDateLabel}
											component="input"
											options={adoptionDateOptions}
											touched={formProps.touched}
											errors={formProps.errors}
											required
											placeholder={labelSettings?.selectDefaultText}
											select
											autoWidth
									/>

								</ContentSpacer>

							<ContentSpacer>
								<br />
								<br />
								<h3>{content?.layDBEmployerAuthorizationTitle}</h3>
								<WysiwygWithReactLink content={content?.layDBEmployerAuthorizationDescription} />
							</ContentSpacer>							
							{
								!showDocusignView &&
								<ContentSpacer>
									<button className='btn-primary' type='button' onClick={() => formProps.submitForm()}>
										<span>{content?.signHereLabel}</span>
									</button>
								</ContentSpacer>
							}
							<ContentSpacer>
								<WysiwygWithReactLink content={content?.signHereDescription} />
							</ContentSpacer>
								</DividerSection>
										{renderDocusignSignView()}
									</FormWrapper>
								</Grid>
							</Form>
						</>
					)}
				</Formik>
			</MainContent>
			<Hidden smDown>
				<SideBarContent>
					<GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
				</SideBarContent>
			</Hidden>
		</PageContainer>
	);

}

export default DbPlanAdoptionForm;